import { useState, useEffect } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Trophy, Award, TrendingUp, Star, Medal } from "lucide-react";
import { BACKEND_URL, API } from "@/lib/api";


export default function Gamification({ eventId }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [allBadges, setAllBadges] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
    fetchBadges();
  }, [eventId]);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/gamification/leaderboard?limit=10`);
      setLeaderboard(response.data);
    } catch (error) {
      console.error("Failed to fetch leaderboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBadges = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/gamification/badges`);
      setAllBadges(response.data);
    } catch (error) {
      console.error("Failed to fetch badges:", error);
    }
  };

  const getRankEmoji = (rank) => {
    if (rank === 1) return "🥇";
    if (rank === 2) return "🥈";
    if (rank === 3) return "🥉";
    return `#${rank}`;
  };

  const getRankColor = (rank) => {
    if (rank === 1) return "from-yellow-500/20 to-orange-500/20 border-yellow-500";
    if (rank === 2) return "from-gray-400/20 to-gray-500/20 border-gray-400";
    if (rank === 3) return "from-amber-600/20 to-amber-700/20 border-amber-600";
    return "from-secondary/20 to-secondary/30 border-border";
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold flex items-center gap-2 mb-2">
          <Trophy className="w-5 h-5 text-primary" />
          Gamification & Leaderboard
        </h3>
        <p className="text-sm text-muted-foreground">
          Sammle Punkte durch Aktivitäten und gewinne Badges! 🏆
        </p>
      </div>

      <Tabs defaultValue="leaderboard" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="leaderboard">
            <Trophy className="w-4 h-4 mr-2" />
            Leaderboard
          </TabsTrigger>
          <TabsTrigger value="badges">
            <Award className="w-4 h-4 mr-2" />
            Badges
          </TabsTrigger>
        </TabsList>

        {/* Leaderboard Tab */}
        <TabsContent value="leaderboard" className="space-y-4">
          {/* Points Info */}
          <div className="p-4 bg-secondary/20 rounded-lg">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Star className="w-4 h-4 text-primary" />
              Punkte sammeln durch:
            </h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">✅ Zusagen (RSVP)</span>
                <span className="font-medium">+5</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">📝 Gästebuch</span>
                <span className="font-medium">+10</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">📸 Selfie</span>
                <span className="font-medium">+15</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">❤️ Like/Kommentar</span>
                <span className="font-medium">+2</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">📋 Bucket List</span>
                <span className="font-medium">+8</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">💬 Chat-Nachricht</span>
                <span className="font-medium">+3</span>
              </div>
            </div>
          </div>

          {/* Leaderboard */}
          <div className="space-y-3">
            {leaderboard.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Trophy className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>Noch keine Teilnehmer</p>
                <p className="text-xs mt-2">Seid die Ersten, die Punkte sammeln! 🎮</p>
              </div>
            ) : (
              leaderboard.map((player) => (
                <div
                  key={player.user_name}
                  className={`p-4 rounded-lg border-2 bg-gradient-to-r ${getRankColor(player.rank)} transition-all hover:scale-[1.02]`}
                >
                  <div className="flex items-center gap-4">
                    {/* Rank */}
                    <div className="text-3xl font-bold w-12 text-center">
                      {getRankEmoji(player.rank)}
                    </div>

                    {/* Player Info */}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-semibold text-lg">{player.user_name}</p>
                        <div className="flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-primary" />
                          <span className="text-2xl font-bold text-primary">{player.total_points}</span>
                          <span className="text-sm text-muted-foreground">Punkte</span>
                        </div>
                      </div>

                      {/* Badges */}
                      {player.badges && player.badges.length > 0 && (
                        <div className="flex gap-1 flex-wrap">
                          {player.badges.slice(0, 5).map((badge, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 bg-primary/10 rounded text-xs font-medium"
                              title={badge.description}
                            >
                              {badge.emoji} {badge.name}
                            </span>
                          ))}
                          {player.badges.length > 5 && (
                            <span className="px-2 py-1 bg-secondary rounded text-xs font-medium">
                              +{player.badges.length - 5} mehr
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>

        {/* Badges Tab */}
        <TabsContent value="badges" className="space-y-4">
          <div className="text-center mb-6">
            <Award className="w-12 h-12 mx-auto mb-2 text-primary" />
            <h4 className="text-lg font-semibold">Verfügbare Badges</h4>
            <p className="text-sm text-muted-foreground">Schalte sie durch Aktivitäten frei!</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(allBadges).map(([badgeId, badge]) => {
              // Check if anyone has earned this badge
              const earnedBy = leaderboard.filter(p => 
                p.badges?.some(b => b.badge_id === badgeId)
              );

              return (
                <div
                  key={badgeId}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    earnedBy.length > 0
                      ? 'bg-primary/5 border-primary/50'
                      : 'bg-secondary/20 border-border'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-4xl">{badge.emoji}</div>
                    <div className="flex-1">
                      <h5 className="font-semibold flex items-center gap-2">
                        {badge.name}
                        {earnedBy.length > 0 && (
                          <span className="px-2 py-0.5 bg-primary text-primary-foreground text-xs rounded-full">
                            {earnedBy.length}x
                          </span>
                        )}
                      </h5>
                      <p className="text-sm text-muted-foreground mb-2">{badge.description}</p>
                      
                      {/* Show who earned it */}
                      {earnedBy.length > 0 && (
                        <div className="flex gap-1 flex-wrap mt-2">
                          {earnedBy.slice(0, 3).map(player => (
                            <span
                              key={player.user_name}
                              className="px-2 py-1 bg-primary/10 text-xs rounded"
                            >
                              {player.user_name}
                            </span>
                          ))}
                          {earnedBy.length > 3 && (
                            <span className="px-2 py-1 bg-secondary text-xs rounded">
                              +{earnedBy.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>
    </Card>
  );
}
